import React, {useState, useEffect, useRef, useCallback} from "react"
import {config} from "../config/environment"
import {
    Badge,
    IconButton,
    Popover,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    Typography,
    Box,
    Divider,
    Button,
    Tabs,
    Tab,
    Chip,
    Tooltip,
    CircularProgress,
    Fade,
    ClickAwayListener,
    TextField,
    InputAdornment,
} from "@mui/material"
import {
    MailOutline as MailIcon,
    CheckCircleOutline as CheckCircleIcon,
    PersonOutline as PersonIcon,
    GroupOutlined as GroupIcon,
    AccessTimeOutlined as AccessTimeIcon,
    CloseOutlined as CloseIcon,
    VolumeUpOutlined as VolumeUpIcon,
    MoreHorizOutlined as MoreHorizIcon,
    SendOutlined as SendIcon,
    SearchOutlined as SearchIcon,
} from "@mui/icons-material"
import {Client} from "@stomp/stompjs"
import {formatDistanceToNow} from "date-fns"
import Cookies from "js-cookie"
import axiosInstance from "../config/axios-config"
import i18n from "../i18n/i18n"
import {useTranslation} from "react-i18next"
import {useNavigate} from "react-router-dom"
import GlowingMessage from "../errors/glowing-message"
import ChatPopup from "../popups/chat-popup"
import {eventBus} from "../event-bus"
import {hy, ru} from "date-fns/locale";

const Message = () => {
    const [messages, setMessages] = useState([])
    const [conversations, setConversations] = useState([])
    const [hasMoreConversations, setHasMoreConversations] = useState(true)
    const [anchorEl, setAnchorEl] = useState(null)
    const [activeTab, setActiveTab] = useState(0)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const stompClientRef = useRef(null)
    const notificationSoundRef = useRef(null)
    const [soundEnabled, setSoundEnabled] = useState(true)
    const [hasMoreMessages, setHasMoreMessages] = useState(true)
    const [page, setPage] = useState(0)
    const [size] = useState(10)
    const [unreadCount, setUnreadCount] = useState(0)
    const [totalCount, setTotalCount] = useState(0)
    const {t} = useTranslation()
    const [stompClient, setStompClient] = useState(null)
    const [isConnected, setIsConnected] = useState(false)
    const [message, setMessage] = useState("")
    const [searchQuery, setSearchQuery] = useState("")
    const [chatPopupOpen, setChatPopupOpen] = useState(false)
    const [selectedConversation, setSelectedConversation] = useState(null)
    const unreadRequest = useRef(0)

    const tenantId = Cookies.get("currentTenantId")

    const getCountByReadState = useCallback(async () => {
        const requestId = ++unreadRequest.current
        try {
            const count = await axiosInstance.get(`/api/v1/conversations/unread-count`, {
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })
            if (requestId === unreadRequest.current) setUnreadCount(count.data)
        } catch (error) {
            console.error("Error fetching messages count:", error)
            setMessage(error.response?.data?.message || "Failed to fetch messages count")
        }
    }, [])

    useEffect(() => {
        getCountByReadState().then((r) => console.log("fetching count of unread messages"))
    }, [messages, getCountByReadState])

    useEffect(() => {
        const handleReadStateChanged = ({tenantId: changedTenantId, conversationId, all}) => {
            if (changedTenantId !== tenantId) return
            getCountByReadState()
            setConversations(previous => previous.map(conversation =>
                all || conversation.conversationId === conversationId
                    ? {...conversation, unreadCount: 0, lastMessage: {...conversation.lastMessage, isRead: true}}
                    : conversation
            ))
        }
        eventBus.on("messageReadStateChanged", handleReadStateChanged)
        return () => {
            eventBus.off("messageReadStateChanged", handleReadStateChanged)
            unreadRequest.current++
        }
    }, [tenantId, getCountByReadState])

    // Memoized filtered messages for better performance
    const getFilteredConversations = useCallback(() => {
        let filtered = conversations

        // Filter by tab
        if (activeTab === 1) {
            filtered = filtered.filter((conv) => !conv.lastMessage?.isRead && conv.lastMessage?.recipientId === tenantId)
        } else if (activeTab === 2) {
            filtered = filtered.filter((conv) => (conv.lastMessage?.isRead && conv.lastMessage?.recipientId === tenantId)
                || conv.lastMessage?.senderId === tenantId)
        }

        // Filter by search query
        if (searchQuery.trim()) {
            filtered = filtered.filter(
                (conv) =>
                    conv.sender?.tenantNameEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    conv.sender?.tenantNameHy.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    conv.sender?.tenantNameRu.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    conv.lastMessage?.text.toLowerCase().includes(searchQuery.toLowerCase())
            )
        }

        return filtered
    }, [conversations, activeTab, searchQuery, messages])

    // Play message sound
    const playMessageSound = useCallback(() => {
        if (!soundEnabled) return

        if (notificationSoundRef.current) {
            notificationSoundRef.current.currentTime = 0
            notificationSoundRef.current.volume = 0.5
            notificationSoundRef.current.play().catch((error) => {
                console.error("Error playing message sound:", error)
            })
        }
    }, [soundEnabled])

    // Toggle sound enabled/disabled
    const toggleSound = useCallback(() => {
        setSoundEnabled((prev) => !prev)
    }, [])

    // Fetch messages from backend
    const fetchConversations = useCallback(
        async (pageNum = 0, append = false) => {
            if (!tenantId) {
                console.warn("No tenant ID found")
                return
            }

            try {
                if (pageNum === 0) {
                    setLoading(true)
                } else {
                    setLoadingMore(true)
                }

                const response = await axiosInstance.get(`/api/v1/conversations`, {
                    params: {
                        page: pageNum,
                        size: size,
                    },
                    headers: {
                        "Language-Context": i18n.language.toUpperCase() || "EN",
                    },
                })

                const {content, last, totalElements} = response.data
                setTotalCount(totalElements)
                setConversations((prev) => (append ? [...prev, ...content] : content))
                setHasMoreConversations(!last)
                setPage(pageNum)
            } catch (error) {
                console.error("Error fetching messages:", error)
                setMessage(error.response?.data?.message || "Failed to fetch messages")
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [tenantId, size],
    )

    // Load more messages
    const handleLoadMore = useCallback(() => {
        if (hasMoreConversations && !loadingMore) {
            fetchConversations(page + 1, true).then((r) => console.log("Loading more messages..."))
        }
    }, [fetchConversations, hasMoreConversations, loadingMore, page])

    // Mark message as read on backend
    const markMessageAsRead = useCallback(async (conversationId) => {
            if (!tenantId) return

            try {
                await axiosInstance.put(
                    `/api/v1/chat-messages/${conversationId}/mark-as-read`,
                    {},
                    {
                        headers: {
                            "Language-Context": i18n.language.toUpperCase() || "EN",
                        },
                    },
                )
                getCountByReadState().then((r) => console.log(`Conversation ${conversationId} marked as read on backend`))
            } catch (error) {
                console.error(`Error marking conversation ${conversationId} as read:`, error)
                setMessage(error.response?.data?.message || `Failed to mark conversation as read`)
            }
        },
        [tenantId],
    )

    // Mark all messages as read on backend
    const markAllMessagesAsRead = useCallback(async () => {
        if (!tenantId) return

        try {
            await axiosInstance.put(
                `/api/v1/messages/read-all`,
                {},
                {
                    headers: {
                        "Language-Context": i18n.language.toUpperCase() || "EN",
                    },
                },
            )
            getCountByReadState().then((r) => console.log("All messages marked as read on backend"))
        } catch (error) {
            setMessage(error.response?.data?.message || "Failed to mark all messages as read")
            console.error("Error marking all messages as read:", error)
        }
    }, [tenantId])

    // Initialize WebSocket connection and fetch initial messages
    useEffect(() => {
        if (!tenantId) {
            console.warn("No tenant ID found in cookies")
            return
        }

        // Setup WebSocket connection
        const client = new Client({
            brokerURL: `${config.WEBSOCKET_URL}/ws`,
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                console.log("Connected to WebSocket")
                setIsConnected(true)

                // Subscribe to messages
                client.subscribe(`/queue/messages/${tenantId}`, (message) => {

                    try {
                        const newMessage = JSON.parse(message.body)

                        setMessages((prev) => [newMessage, ...prev])

                        // Play notification sound for new notifications
                        playMessageSound()

                    } catch (error) {
                        console.error("Error parsing notification:", error)
                    }

                    console.log("New message received via WebSocket:", message.body)
                    fetchConversations(0, false).then((r) => console.log("New message received, fetching conversations"))
                })
            },
            onDisconnect: () => {
                console.log("Disconnected from WebSocket")
                setIsConnected(false)
            },
            onStompError: (frame) => {
                console.error("STOMP Error:", frame)
                setIsConnected(false)
            },
            onWebSocketError: (error) => {
                console.error("WebSocket Error:", error)
                setIsConnected(false)
            },
        })

        client.activate()
        setStompClient(client)
        stompClientRef.current = client

        return () => {
            if (client) {
                client.deactivate()
            }
        }
    }, [tenantId, playMessageSound, fetchConversations])

    const handleMessageClickButton = useCallback(
        (event) => {
            setAnchorEl(event.currentTarget)
            fetchConversations(0, false).then((r) => console.log("Fetching messages on button click"))
        },
        [fetchConversations],
    )

    const handleClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const handleTabChange = useCallback((event, newValue) => {
        setActiveTab(newValue)
    }, [])

    // Mark a single message as read
    const handleMessageRead = useCallback((conversationId) => {

            markMessageAsRead(conversationId).then((r) => "call to backend to mark message as read")
        },
        [markMessageAsRead],
    )

    const handleMarkAllAsRead = useCallback(() => {
        setMessages((prev) => prev.map((msg) => ({...msg, isRead: true, unreadCount: 0})))
        markAllMessagesAsRead().then((r) => console.log("Marking all messages as read"))
    }, [markAllMessagesAsRead])

    const handleConversationClick = useCallback(async (conversation) => {

            // Mark as read only if not already read
            if (!conversation.lastMessage?.isRead && conversation.lastMessage?.recipientId === tenantId) {
                handleMessageRead(conversation.conversationId)
            }

            // Close popover and open chat popup
            handleClose()
            setSelectedConversation(conversation)
            setChatPopupOpen(true)
        },
        [handleMessageRead, handleClose],
    )

    const handleChatPopupClose = useCallback(() => {
        setChatPopupOpen(false)
        setSelectedConversation(null)
    }, [])

    const getLocale = () => {
        switch (i18n.language) {
            case "ru":
                return ru;
            case "hy":
                return hy;
            default:
                return undefined; // defaults to English
        }
    };

    const formatMessageDate = useCallback((dateString) => {
        try {
            const date = new Date(dateString)
            return formatDistanceToNow(date, {addSuffix: true, locale: getLocale()})
        } catch (error) {
            return "Unknown time"
        }
    }, [])

    const open = Boolean(anchorEl)
    const id = open ? "message-popover" : undefined
    const filteredConversations = getFilteredConversations()

    const navigate = useNavigate()

    const handleViewAllMessages = useCallback(() => {
        handleClose()
        navigate("/messages")
    }, [handleClose, navigate])


    const addressToString = (address) => {
        if (!address) return "";
        return `${address.street || ""}, ${address.city || ""}, ${address.country || ""}`.trim();
    }

    return (
        <>
            {/* Hidden audio element for message sound */}
            <audio ref={notificationSoundRef} preload="auto" style={{display: "none"}}>
                <source src="/notification.mp3" type="audio/mpeg"/>
            </audio>

            <GlowingMessage message={message} onClose={() => setMessage("")}/>
            <Tooltip title={`${t("Messages")}${unreadCount > 0 ? ` (${unreadCount} ${t("unread")})` : ""}`}>
                <IconButton
                    color="inherit"
                    onClick={handleMessageClickButton}
                    aria-describedby={id}
                    aria-label={`${t("Messages")}${unreadCount > 0 ? ` (${unreadCount} ${t("unread")})` : ""}`}
                    sx={{
                        position: "relative",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                            transform: "scale(1.05)",
                            bgcolor: "action.hover",
                        },
                        "&:focus": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: "2px",
                        },
                    }}
                >
                    <Badge
                        badgeContent={unreadCount > 99 ? "99+" : unreadCount > 0 ? unreadCount : null}
                        color="error"
                        overlap="circular"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.7rem",
                                height: "16px",
                                minWidth: "16px",
                                padding: "0 6px",
                            },
                        }}
                    >
                        <MailIcon/>
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                disableScrollLock={true}
                disableRestoreFocus={true}
                marginThreshold={16}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxHeight: 600,
                        borderRadius: 3,
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                        overflow: "hidden",
                        display: "flex",
                        flexDirection: "column",
                        border: "1px solid",
                        borderColor: "divider",
                    },
                }}
                TransitionComponent={Fade}
                transitionDuration={200}
            >
                <ClickAwayListener onClickAway={handleClose}>
                    <Box sx={{display: "flex", flexDirection: "column", height: "100%", maxHeight: 600}}>
                        {/* Header */}
                        <Box
                            sx={{
                                background: "linear-gradient(to right, #f5f7fa, #e4e8f0)",
                                color: "black",
                                p: 2.5,
                                flexShrink: 0,
                                position: "relative",
                            }}
                        >
                            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2}}>
                                <Box>
                                    <Typography variant="h6" sx={{fontWeight: 600}}>
                                        {t("Messages")}
                                    </Typography>
                                </Box>
                                <Box sx={{display: "flex", gap: 0.5}}>
                                    <Tooltip title={soundEnabled ? t("SoundOn") : t("SoundOff")}>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: soundEnabled ? "inherit" : "text.disabled",
                                                "&:hover": {bgcolor: "rgba(0, 0, 0, 0.04)"},
                                            }}
                                            onClick={toggleSound}
                                        >
                                            <VolumeUpIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("MarkAllAsRead")}>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: "inherit",
                                                "&:hover": {bgcolor: "rgba(0, 0, 0, 0.04)"},
                                                "&:disabled": {color: "rgba(0, 0, 0, 0.26)"},
                                            }}
                                            onClick={handleMarkAllAsRead}
                                            disabled={unreadCount === 0}
                                        >
                                            <CheckCircleIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={t("Close")}>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: "inherit",
                                                "&:hover": {bgcolor: "rgba(0, 0, 0, 0.04)"},
                                            }}
                                            onClick={handleClose}
                                        >
                                            <CloseIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <TextField
                                fullWidth
                                size="small"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={t("SearchChats") || "Search chats..."}
                                variant="outlined"
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                        bgcolor: "rgba(255, 255, 255, 0.8)",
                                        "&:hover": {
                                            bgcolor: "rgba(255, 255, 255, 0.9)",
                                        },
                                        "&.Mui-focused": {
                                            bgcolor: "white",
                                        },
                                    },
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{color: "text.secondary"}}/>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>

                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant={i18n.language !== "en" ? "scrollable" : "fullWidth"}
                            sx={{
                                borderBottom: 1,
                                borderColor: "divider",
                                flexShrink: 0,
                                bgcolor: "background.paper",
                                "& .MuiTab-root": {
                                    minHeight: 48,
                                    textTransform: "none",
                                    fontWeight: 500,
                                },
                            }}
                        >
                            <Tab
                                label={
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        {t("All")}
                                        <Chip
                                            label={totalCount}
                                            size="small"
                                            variant="outlined"
                                            sx={{height: 20, fontSize: "0.7rem"}}
                                        />
                                    </Box>
                                }
                            />
                            <Tab
                                label={
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        {t("unread")}
                                        {unreadCount > 0 && (
                                            <Chip
                                                label={unreadCount}
                                                size="small"
                                                color="error"
                                                sx={{height: 20, fontSize: "0.7rem"}}
                                            />
                                        )}
                                    </Box>
                                }
                            />
                            <Tab
                                label={
                                    <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                        {t("read")}
                                        <Chip
                                            label={totalCount - unreadCount}
                                            size="small"
                                            variant="outlined"
                                            sx={{height: 20, fontSize: "0.7rem"}}
                                        />
                                    </Box>
                                }
                            />
                        </Tabs>


                        {/* Content area */}
                        <Box
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                minHeight: 0,
                                maxHeight: "calc(600px - 200px)", // Adjusted for search input
                                overflow: "hidden",
                            }}
                        >
                            {loading ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        p: 4,
                                        flex: 1,
                                    }}
                                >
                                    <CircularProgress size={40}/>
                                    <Typography variant="body2" sx={{ml: 2}}>
                                        {t("loadingMessages")}
                                    </Typography>
                                </Box>
                            ) : filteredConversations.length === 0 ? (
                                <Box
                                    sx={{
                                        p: 4,
                                        textAlign: "center",
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Box sx={{color: "text.secondary", mb: 2}}>
                                        <MailIcon sx={{fontSize: 64, opacity: 0.5}}/>
                                    </Box>
                                    <Typography variant="h6" color="text.secondary" gutterBottom>
                                        {t("NoMessages")}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {activeTab === 0
                                            ? t("AllMessagesDesc")
                                            : activeTab === 1
                                                ? t("UnreadMessagesDesc")
                                                : t("ReadMessagesDesc")}
                                    </Typography>
                                </Box>
                            ) : (
                                <List
                                    sx={{
                                        p: 0,
                                        height: "100%",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                        "&::-webkit-scrollbar": {
                                            width: "6px",
                                        },
                                        "&::-webkit-scrollbar-track": {
                                            background: "transparent",
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            background: "rgba(0,0,0,0.2)",
                                            borderRadius: "3px",
                                        },
                                        "&::-webkit-scrollbar-thumb:hover": {
                                            background: "rgba(0,0,0,0.3)",
                                        },
                                    }}
                                >
                                    {filteredConversations.map((conversation, index) => (
                                        <React.Fragment key={conversation.conversationId || index}>
                                            <ListItem
                                                alignItems="flex-start"
                                                sx={{
                                                    px: 2,
                                                    py: 1.5,
                                                    cursor: "pointer",
                                                    bgcolor: conversation.lastMessage?.isRead ? "transparent" : "action.hover",
                                                    "&:hover": {
                                                        bgcolor: "action.selected",
                                                    },
                                                    transition: "all 0.2s ease",
                                                    position: "relative",
                                                }}
                                                onClick={() => handleConversationClick(conversation)}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={conversation.senderAvatar}
                                                        sx={{
                                                            bgcolor: "primary.main",
                                                            border: "2px solid",
                                                            borderColor: conversation.lastMessage?.isRead && conversation.lastMessage?.recipientId === tenantId ? "divider" : "primary.main",
                                                        }}
                                                    >
                                                        {/*{getMessageIcon(messageItem.messageType, messageItem.isOnline)}*/}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: "space-between",
                                                                alignItems: "flex-start",
                                                            }}
                                                        >
                                                            <Box sx={{flex: 1, pr: 1}}>
                                                                {/* Tenant name */}
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    sx={{
                                                                        fontWeight:
                                                                            conversation.lastMessage?.isRead ||
                                                                            conversation.lastMessage?.recipientId !== tenantId
                                                                                ? 400
                                                                                : 600,
                                                                        color: "text.primary",
                                                                        mb: 0.3,
                                                                    }}
                                                                >
                                                                    {conversation.sender?.tenantNameEn}
                                                                </Typography>

                                                                {/* Address (small and lighter) */}
                                                                {conversation.sender?.address && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            lineHeight: 1.3,
                                                                            fontSize: "0.75rem",
                                                                            mb: 0.5,
                                                                            whiteSpace: "nowrap",
                                                                            overflow: "hidden",
                                                                            textOverflow: "ellipsis",
                                                                        }}
                                                                    >
                                                                        {addressToString(conversation.sender.address)}
                                                                    </Typography>
                                                                )}
                                                            </Box>

                                                            {/* Unread count chip */}
                                                            {conversation.unreadCount > 0 && (
                                                                <Chip
                                                                    label={conversation.unreadCount}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{height: 18, fontSize: "0.6rem"}}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <>
                                                            {/* Message text preview */}
                                                            <Typography
                                                                variant="body2"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display: "-webkit-box",
                                                                    WebkitLineClamp: 2, // show max 2 lines
                                                                    WebkitBoxOrient: "vertical",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    lineHeight: 1.4,
                                                                    mb: 0.8,
                                                                }}
                                                            >
                                                                {conversation.lastMessage?.text}
                                                            </Typography>

                                                            {/* Time below message */}
                                                            <Typography
                                                                variant="caption"
                                                                color="text.secondary"
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                <AccessTimeIcon fontSize="inherit"
                                                                                sx={{mr: 0.5, fontSize: "1rem"}}/>
                                                                {formatMessageDate(conversation.lastMessage?.createdAt)}
                                                            </Typography>
                                                        </>
                                                    }
                                                />

                                                {!conversation.lastMessage?.isRead && conversation.lastMessage?.recipientId === tenantId && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            right: 8,
                                                            top: "50%",
                                                            transform: "translateY(-50%)",
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            bgcolor: "primary.main",
                                                        }}
                                                    />
                                                )}
                                            </ListItem>
                                            {index < conversations.length - 1 && <Divider component="li" sx={{mx: 2}}/>}
                                        </React.Fragment>
                                    ))}

                                    {/* Load More Button */}
                                    {hasMoreConversations && (
                                        <Box sx={{p: 2, textAlign: "center"}}>
                                            <Button
                                                variant="text"
                                                color="primary"
                                                onClick={handleLoadMore}
                                                disabled={loadingMore}
                                                startIcon={loadingMore ? <CircularProgress size={16}/> :
                                                    <MoreHorizIcon/>}
                                                sx={{textTransform: "none"}}
                                            >
                                                {loadingMore ? t("Loading") : t("SeeMore")}
                                            </Button>
                                        </Box>
                                    )}
                                </List>
                            )}
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                p: 2,
                                borderTop: 1,
                                borderColor: "divider",
                                bgcolor: "grey.50",
                                flexShrink: 0,
                            }}
                        >
                            <Button
                                fullWidth
                                variant="contained"
                                size="medium"
                                onClick={handleViewAllMessages}
                                startIcon={<SendIcon/>}
                                sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 600,
                                }}
                            >
                                {t("ViewAllMessages")}
                            </Button>
                        </Box>
                    </Box>
                </ClickAwayListener>
            </Popover>

            {chatPopupOpen && selectedConversation && (
                <ChatPopup open={chatPopupOpen} onClose={handleChatPopupClose} conversation={selectedConversation}
                           onTyping={handleMessageRead(selectedConversation.conversationId)}/>
            )}
        </>
    )
}

export default Message
